import { z } from 'zod';
import {
  SURVEY_NUMERIC_ANSWER_TYPES,
  SURVEY_PERCENTAGE_MAX,
  SURVEY_PERCENTAGE_MIN,
} from '../constants/survey.constants';
import {
  AnswerType,
  QuestionType,
  type SubmitSurveyPayload,
  type SurveyAnswerFormValues,
  type SurveyQuestionDetail,
} from '../types/survey.types';

export function answerFieldName(questionId: number): string {
  return `q_${questionId}`;
}

export function isNumericTextQuestion(question: SurveyQuestionDetail): boolean {
  return question.answerType !== null && SURVEY_NUMERIC_ANSWER_TYPES.includes(question.answerType);
}

function buildRequiredStringSchema(
  question: SurveyQuestionDetail,
  requiredMessage: string,
): z.ZodTypeAny {
  const schema = z.string();
  return question.isRequired ? schema.min(1, requiredMessage) : schema;
}

function buildCheckboxSchema(question: SurveyQuestionDetail): z.ZodTypeAny {
  const schema = z.array(z.string());
  return question.isRequired ? schema.min(1, 'Please select at least one option') : schema;
}

function buildTextSchema(question: SurveyQuestionDetail): z.ZodTypeAny {
  return z.string().superRefine((value, ctx) => {
    const trimmed = value.trim();
    if (!trimmed) {
      if (question.isRequired) {
        ctx.addIssue({ code: 'custom', message: 'This field is required' });
      }
      return;
    }
    if (question.maxChars !== null && trimmed.length > question.maxChars) {
      ctx.addIssue({
        code: 'custom',
        message: `Must be at most ${question.maxChars} characters`,
      });
    }
  });
}

const INTEGER_PATTERN = /^-?\d+$/;
const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

function getNumericBounds(question: SurveyQuestionDetail): {
  min: number | null;
  max: number | null;
} {
  if (question.answerType === AnswerType.PERCENTAGE) {
    return {
      min: question.minValue ?? SURVEY_PERCENTAGE_MIN,
      max: question.maxValue ?? SURVEY_PERCENTAGE_MAX,
    };
  }
  return { min: question.minValue, max: question.maxValue };
}

function buildNumericSchema(question: SurveyQuestionDetail): z.ZodTypeAny {
  const isInteger = question.answerType === AnswerType.NUMBER;
  const pattern = isInteger ? INTEGER_PATTERN : DECIMAL_PATTERN;

  return z.string().superRefine((value, ctx) => {
    const trimmed = value.trim();
    if (!trimmed) {
      if (question.isRequired) {
        ctx.addIssue({ code: 'custom', message: 'This field is required' });
      }
      return;
    }
    if (!pattern.test(trimmed)) {
      ctx.addIssue({
        code: 'custom',
        message: isInteger ? 'Must be a whole number' : 'Must be a number',
      });
      return;
    }
    const numericValue = Number(trimmed);
    const { min, max } = getNumericBounds(question);
    if (min !== null && numericValue < min) {
      ctx.addIssue({ code: 'custom', message: `Must be at least ${min}` });
    }
    if (max !== null && numericValue > max) {
      ctx.addIssue({ code: 'custom', message: `Must be at most ${max}` });
    }
  });
}

function buildFieldSchema(question: SurveyQuestionDetail): z.ZodTypeAny {
  switch (question.type) {
    case QuestionType.CHECKBOX:
      return buildCheckboxSchema(question);
    case QuestionType.MCQ:
    case QuestionType.DROPDOWN:
      return buildRequiredStringSchema(question, 'Please select an option');
    case QuestionType.RATING:
      return buildRequiredStringSchema(question, 'Please select a rating');
    case QuestionType.TEXT:
      return isNumericTextQuestion(question)
        ? buildNumericSchema(question)
        : buildTextSchema(question);
  }
}

export function buildAnswerFormSchema(questions: SurveyQuestionDetail[]) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const question of questions) {
    shape[answerFieldName(question.id)] = buildFieldSchema(question);
  }
  return z.object(shape);
}

export function buildDefaultAnswerValues(
  questions: SurveyQuestionDetail[],
): SurveyAnswerFormValues {
  const values: SurveyAnswerFormValues = {};
  for (const question of questions) {
    values[answerFieldName(question.id)] = question.type === QuestionType.CHECKBOX ? [] : '';
  }
  return values;
}

export function toSubmitSurveyPayload(
  questions: SurveyQuestionDetail[],
  values: SurveyAnswerFormValues,
): SubmitSurveyPayload {
  const answers: SubmitSurveyPayload['answers'] = [];

  for (const question of questions) {
    const raw = values[answerFieldName(question.id)];

    if (question.type === QuestionType.CHECKBOX) {
      const optionIds = (raw as string[]).map(Number).filter(Number.isFinite);
      if (optionIds.length > 0) {
        answers.push({ questionId: question.id, optionIds });
      }
      continue;
    }

    if (question.type === QuestionType.MCQ || question.type === QuestionType.DROPDOWN) {
      const value = raw as string;
      if (value) {
        answers.push({ questionId: question.id, optionIds: [Number(value)] });
      }
      continue;
    }

    const value = (raw as string).trim();
    if (value) {
      answers.push({ questionId: question.id, answerValue: value });
    }
  }

  return { answers };
}
